import { DEPENDENCY_PATH_COLORS, DEPENDENCY_DEPTH_OPACITY } from '@/constants/timeline';

export interface DependencyPathLegendProps {
  /** Whether the legend is visible (only show when a path is active) */
  isActive: boolean;
  /** Optional additional className */
  className?: string;
}

/**
 * Legend item for dependency path visualization
 */
function PathLegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-0.5 w-6 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="whitespace-nowrap text-text-muted">{label}</span>
    </div>
  );
}

/**
 * Depth indicator showing opacity levels
 */
function DepthIndicator() {
  const levels = DEPENDENCY_DEPTH_OPACITY.slice(1); // Skip index 0 (selected item)

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {levels.map((opacity, index) => (
          <div
            key={index}
            className="size-2 rounded-sm bg-text-secondary"
            style={{ opacity }}
            title={`${index + 1} hop${index > 0 ? 's' : ''} away`}
          />
        ))}
      </div>
      <span className="whitespace-nowrap text-text-muted">= distance</span>
    </div>
  );
}

/**
 * DependencyPathLegend molecule - explains the dependency path color scheme
 *
 * Shows when a deliverable is selected to explain:
 * - Blocking (upstream) color
 * - Following (downstream) color
 * - Depth/opacity meaning
 */
export function DependencyPathLegend({ isActive, className = '' }: DependencyPathLegendProps) {
  if (!isActive) return null;

  return (
    <div
      className={`
        flex flex-wrap items-center gap-4 rounded-lg border border-border-muted bg-bg-subtle/50 px-3 py-2 text-xs
        ${className}
      `}
    >
      <span className="font-medium text-text-secondary">Path:</span>
      <PathLegendItem color={DEPENDENCY_PATH_COLORS.blocking.line} label="Blocking" />
      <PathLegendItem color={DEPENDENCY_PATH_COLORS.selected.line} label="Selected" />
      <PathLegendItem color={DEPENDENCY_PATH_COLORS.following.line} label="Following" />
      <DepthIndicator />
    </div>
  );
}
